import { useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getCompanyRevenue } from "@/services/firebase/revenue";
import { exportToCSV } from "@/utils/csvExport";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Coins, Download, ArrowLeft, CreditCard, Hash } from "lucide-react";
import { AdminPage } from "@/components/admin-ui/admin-page";
import { AdminPageHeader } from "@/components/admin-ui/admin-page-header";
import { AdminStatsCard } from "@/components/admin-ui/admin-stats-card";
import { AdminDataTable } from "@/components/admin-ui/admin-data-table";
import type { AdminTableColumn } from "@/components/admin-ui/admin-data-table";
import { AdminLoadingSkeleton } from "@/components/admin-ui/admin-loading-skeleton";
import { AdminErrorAlert } from "@/components/admin-ui/admin-error-alert";
import { AdminEmptyState } from "@/components/admin-ui/admin-empty-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import FinancialDateRangePicker from "@/components/financial/FinancialDateRangePicker";
import { useFinancialDateRange } from "@/store/financialDateRange";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const dramaColumns: AdminTableColumn[] = [
  { key: "drama_title", label: "劇集", sortable: true },
  { key: "paid_coins", label: "付費金幣", sortable: true },
  { key: "free_coins", label: "免費金幣", sortable: true, hideBelow: "md" },
  { key: "transaction_count", label: "交易數", sortable: true },
];

export default function CompanyRevenuePage() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { start, end, setRange } = useFinancialDateRange();

  useEffect(() => {
    const urlStart = searchParams.get("start");
    const urlEnd = searchParams.get("end");
    if (
      urlStart && urlEnd &&
      DATE_RE.test(urlStart) && DATE_RE.test(urlEnd) &&
      (urlStart !== start || urlEnd !== end)
    ) {
      setRange(urlStart, urlEnd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchParams.get("start") !== start || searchParams.get("end") !== end) {
      const next = new URLSearchParams(searchParams);
      next.set("start", start);
      next.set("end", end);
      setSearchParams(next, { replace: true });
    }
  }, [start, end, searchParams, setSearchParams]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["company-revenue", companyId, start, end],
    queryFn: () => getCompanyRevenue(companyId!, { startDate: start, endDate: end }),
    enabled: !!companyId && !!start && !!end,
  });

  const dramas = data?.dramas || [];

  const handleExport = () => {
    if (!dramas.length) return;
    exportToCSV(
      dramas,
      [
        { key: "drama_id", label: "劇集ID" },
        { key: "drama_title", label: "劇集" },
        { key: "paid_coins", label: "付費金幣" },
        { key: "free_coins", label: "免費金幣" },
        { key: "transaction_count", label: "交易數" },
      ],
      `company_revenue_${companyId}_${start}_${end}`
    );
  };

  return (
    <AdminPage>
      <AdminPageHeader
        title="公司營收詳情"
        subtitle={`Company ID: ${companyId}`}
        loading={isLoading}
        onRefresh={() => refetch()}
        actions={
          <>
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate("/financial/revenue")}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <FinancialDateRangePicker />
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={!dramas.length}
            >
              <Download className="h-4 w-4 mr-1" />
              CSV 匯出
            </Button>
          </>
        }
      />

      {isLoading && <AdminLoadingSkeleton variant="mixed" />}

      {error && <AdminErrorAlert message={`載入失敗: ${(error as Error).message}`} />}

      {data && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4">
            <AdminStatsCard
              icon={<CreditCard className="h-4 w-4" />}
              label="付費金幣"
              value={(data.total_paid_coins || 0).toLocaleString()}
            />
            <AdminStatsCard
              icon={<Coins className="h-4 w-4" />}
              label="免費金幣"
              value={(data.total_free_coins || 0).toLocaleString()}
            />
            <AdminStatsCard
              icon={<Hash className="h-4 w-4" />}
              label="交易筆數"
              value={(data.transaction_count || 0).toLocaleString()}
            />
          </div>

          {/* Drama Bar Chart */}
          {dramas.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">劇集營收分佈</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={dramas.slice(0, 20)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="drama_title" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={80} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="paid_coins" name="付費金幣" fill="#b45309" />
                    <Bar dataKey="free_coins" name="免費金幣" fill="#a8a29e" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Drama Table */}
          <AdminDataTable
            columns={dramaColumns}
            data={dramas}
            emptyTitle="暫無數據"
            pageSize={20}
            defaultSort={{ key: "paid_coins", dir: "desc" }}
            getRowId={(item: any) => item.drama_id}
            onRowClick={(item: any) => navigate(`/financial/revenue/drama/${item.drama_id}?start=${start}&end=${end}`)}
            renderCell={(item: any, col) => {
              switch (col.key) {
                case "drama_title":
                  return <span className="font-medium">{item.drama_title || item.drama_id}</span>;
                case "paid_coins":
                  return <span className="text-right block">{item.paid_coins.toLocaleString()}</span>;
                case "free_coins":
                  return <span className="text-right block text-muted-foreground">{item.free_coins.toLocaleString()}</span>;
                case "transaction_count":
                  return <span className="text-right block text-muted-foreground">{item.transaction_count}</span>;
                default:
                  return null;
              }
            }}
          />
        </div>
      )}
    </AdminPage>
  );
}
